// Shared sessionStorage cache for read-mostly API responses.
//
// Why sessionStorage instead of localStorage: we want the cache to clear when
// the tab closes so students never see multi-day-old university data.
//
// Contract:
//   const { data, loading, fromCache, refetch } = useCachedFetch(
//     key,           // stable string, null when caller has no data yet
//     fetchFn,       // async () => the raw response
//   );
//
// The caller is responsible for keying — usually
//   `wua:<page>:<userId>:<hash-of-inputs>`
// so profile edits or filter changes automatically invalidate.
//
// Mutations (pin / unpin / rescore) should call `bustCachePrefix("wua:shortlist:")`
// so the *next* revisit refetches fresh data.

import { useCallback, useEffect, useState } from "react";

const isBrowser = typeof window !== "undefined" && !!window.sessionStorage;

export function readCache(key) {
  if (!key || !isBrowser) return null;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeCache(key, value) {
  if (!key || !isBrowser) return;
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
}

/** Remove every sessionStorage entry whose key starts with the given prefix. */
export function bustCachePrefix(prefix) {
  if (!isBrowser || !prefix) return;
  const doomed = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(prefix)) doomed.push(k);
  }
  doomed.forEach((k) => sessionStorage.removeItem(k));
}

/**
 * useCachedFetch — hydrates from sessionStorage instantly when a cache entry
 * exists for `key`, otherwise runs `fetchFn` and stores the result.
 *
 * `fetchFn` must be stable across renders (wrap in useCallback in the caller).
 */
export function useCachedFetch(key, fetchFn) {
  const [data, setData] = useState(() => readCache(key));
  const [loading, setLoading] = useState(() => !readCache(key));
  const [fromCache, setFromCache] = useState(() => !!readCache(key));

  const run = useCallback(
    async ({ force = false } = {}) => {
      if (!force) {
        const cached = readCache(key);
        if (cached !== null) {
          setData(cached);
          setLoading(false);
          setFromCache(true);
          return cached;
        }
      }
      setLoading(true);
      setFromCache(false);
      const fresh = await fetchFn();
      setData(fresh);
      writeCache(key, fresh);
      setLoading(false);
      return fresh;
    },
    [key, fetchFn],
  );

  useEffect(() => {
    if (!key) { setLoading(false); return; }
    run().catch(() => setLoading(false));
  }, [key, run]);

  return { data, setData, loading, fromCache, refetch: () => run({ force: true }) };
}

/** Small deterministic hash — same helper AdvancedResults uses to key by profile. */
export function stableHash(obj) {
  const canonical = JSON.stringify(obj, (_k, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {});
    }
    return v;
  });
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}
